import { MODULOS } from './navegacion';

/**
 * EL RECORRIDO GUIADO DEL PORTAL — el guion, aparte del motor.
 *
 * Pedido por la usuaria el 2026-08-26: «crea un video de recorrido por el
 * portal … que cuando un usuario entre por primera vez le vaya mostrando todo
 * lo que tiene el portal y para qué sirve», con las capturas de otro portal
 * (data.farmatodomedia.com) como referencia: una tarjeta que señala una parte de
 * la pantalla, con «Paso 3 de 11» y los botones Anterior / Siguiente.
 *
 * ── POR QUÉ NO ES UN VÍDEO ──────────────────────────────────────────────────
 * Ella lo llamó «video», y las capturas que mandó no son un vídeo: son un
 * recorrido interactivo sobre la pantalla de verdad. Se hace así, y no con un
 * MP4, por tres razones que se pagan solas:
 *   · **Cada usuario ve SU portal.** Un asesor tiene 4 módulos y MASTER 14; un
 *     vídeo tendría que enseñar los 14 a todo el mundo, y estaría enseñándole a
 *     un asesor secciones a las que no puede entrar. Aquí los pasos de módulo se
 *     filtran por el propio permiso del usuario (`pasosVisibles`).
 *   · **No envejece.** El portal cambió 14 veces este mes; un vídeo grabado
 *     hoy miente en septiembre y nadie se entera. Este guion vive junto al
 *     código y hay una prueba que falla si se añade un módulo sin su paso.
 *   · **Pesa cero.** Un vídeo de un minuto son decenas de MB por cada visita, y
 *     la fuerza de ventas entra desde el teléfono con datos móviles.
 *
 * ── CÓMO SE ATA A LA PANTALLA ───────────────────────────────────────────────
 * Cada paso apunta a un elemento REAL por `data-recorrido="…"`. Se eligió un
 * atributo propio y no una clase de Tailwind ni una estructura de DOM: las
 * clases cambian al retocar el diseño y el recorrido se quedaría señalando el
 * aire. Si el elemento no está en la pantalla en ese momento —el menú lateral
 * no existe en las páginas de un solo submódulo—, **el paso se salta y el
 * contador se recalcula**: nunca se dice «Paso 4 de 11» y se señala la nada.
 *
 * El CONTENIDO no lleva ninguna cifra ni ningún código de vendedor: describe
 * para qué sirve cada sección, así que vale igual para los 40 usuarios y la
 * NORMA 1 (un asesor solo ve lo suyo) no se ve afectada.
 */
export interface PasoRecorrido {
  /** Estable: se usa en las pruebas y para recordar por dónde iba. */
  id: string;
  titulo: string;
  texto: string;
  /**
   * Selector del elemento que se resalta. Sin él, la tarjeta va centrada en la
   * pantalla (el saludo y el cierre, que no hablan de ningún botón).
   */
  objetivo?: string;
  /**
   * Solo se ofrece a quien tenga este módulo. Es el mismo id de `MODULOS`
   * (`lib/navegacion.ts`) y de `auth.modulo`, así que el recorrido no puede
   * hablarle a nadie de una sección a la que no entra.
   */
  modulo?: string;
}

/** Los pasos que hablan de la pantalla, no de un módulo. */
const GENERALES_INICIO: PasoRecorrido[] = [
  {
    id: 'marca',
    objetivo: '[data-recorrido="marca"]',
    titulo: 'Un solo sitio para las tres empresas',
    texto: 'Este es el Portal Master de Grupo DH: DDH, DH Vital y Difranca juntas. '
      + 'Todo lo que veas aquí sale de la misma base de datos del grupo, así que la '
      + 'cifra que mires es la misma que mira gerencia.',
  },
  {
    id: 'modulos',
    objetivo: '[data-recorrido="modulos"]',
    titulo: 'Los módulos: cada uno es un área',
    texto: 'Esta barra son las áreas del negocio. Solo aparecen las que tu usuario '
      + 'tiene concedidas, y al pulsar una cambia el menú de la izquierda. '
      + 'Si son muchas, la barra se desliza de lado.',
  },
];

/** Un paso por MÓDULO. La prueba `recorrido.test.ts` exige que no falte ninguno. */
const POR_MODULO: PasoRecorrido[] = [
  {
    id: 'mod-resumen',
    modulo: 'resumen',
    titulo: 'Resumen General',
    texto: 'Cómo va el grupo: la venta del día y del mes, la evolución mes a mes, la '
      + 'proyección de cierre, las descargas del período y los hallazgos que el portal '
      + 'detecta solo (subidas, caídas y clientes que dejaron de comprar).',
  },
  {
    id: 'mod-comercial',
    modulo: 'comercial',
    titulo: 'Comercial',
    texto: 'Tu día de calle. Entra por los 7 pasos de la visita y trae además tus KPI, '
      + 'el Reporte Flash de ventas, metas, lanzamientos, clientes, los comités y las '
      + 'negociaciones comerciales.',
  },
  {
    id: 'mod-cei',
    modulo: 'cei',
    titulo: 'C.E.I. — Centro de Estadística',
    texto: 'El taller de análisis: vendedores, clientes, marcas, metas, KPI, el análisis '
      + 'financiero, Power Query y la analítica avanzada (Pareto, estacionalidad, '
      + 'comparador y escenarios).',
  },
  {
    id: 'mod-cobranzas',
    modulo: 'cobranzas',
    titulo: 'Cobranzas',
    texto: 'La cartera de clientes de punta a punta: cuentas por cobrar, días en calle, '
      + 'facturas cerradas, el reporte de pago con sus soportes, la planificación de rutas '
      + 'y la inactivación de clientes.',
  },
  {
    id: 'mod-almacen',
    modulo: 'almacen',
    titulo: 'Almacén y Despacho',
    texto: 'Las devoluciones de mercancía con su circuito de aprobación y su acta en PDF, '
      + 'los catálogos de venta y la ficha del prospecto de cliente nuevo.',
  },
  {
    id: 'mod-fact',
    modulo: 'fact',
    titulo: 'Facturación',
    texto: 'Las notas de crédito de las negociaciones aprobadas —con su número y su '
      + 'soporte— y los catálogos de venta.',
  },
  {
    id: 'mod-finanzas',
    modulo: 'finanzas',
    titulo: 'Finanzas',
    texto: 'El análisis financiero por cliente, marca y documento; los viáticos y gastos; '
      + 'y las notas de crédito de las negociaciones.',
  },
  {
    id: 'mod-gerencia',
    modulo: 'gerencia',
    titulo: 'Gerencia',
    texto: 'La vista de gerencia: el análisis comercial y financiero del grupo, con las '
      + 'marcas, las metas, los lanzamientos y Go To Market en un mismo sitio.',
  },
  {
    id: 'mod-intel',
    modulo: 'intel',
    titulo: 'Inteligencia de Mercado',
    texto: 'Qué se vende, dónde y contra qué: marcas, lanzamientos, metas y la analítica '
      + 'del período.',
  },
  {
    id: 'mod-tmk',
    modulo: 'tmk',
    titulo: 'Trade Marketing',
    texto: 'El material de venta por marca, los catálogos de pedido, Go To Market, los '
      + 'lanzamientos y el seguimiento de Trade.',
  },
  {
    id: 'mod-medios',
    modulo: 'medios',
    titulo: 'Medios y Comunicación',
    texto: 'Los prospectos que llegan por las redes sociales de las 5 marcas: se cargan aquí, '
      + 'el portal los direcciona solo al asesor de la zona y a su coordinador, y el asesor '
      + 'registra si el negocio aplicó o no dentro de su plazo de visita.',
  },
  {
    id: 'mod-contraloria',
    modulo: 'contraloria',
    titulo: 'Contraloría',
    texto: 'La bitácora de usuarios: quién entró, qué cargó, qué aprobó y qué descargó, '
      + 'con su fecha, su hora y desde dónde lo hizo.',
  },
  {
    id: 'mod-ctor',
    modulo: 'ctor',
    titulo: 'Constructor',
    texto: 'Armas tu propio cuadro: eliges las medidas y las dimensiones y el portal lo '
      + 'calcula sobre la base fiscal, con años que los demás módulos no ofrecen.',
  },
  {
    id: 'mod-info',
    modulo: 'info',
    titulo: 'Información de Interés',
    texto: 'Los documentos del grupo: normativas, formatos, listas de precios y material '
      + 'de apoyo. Se abren y se descargan desde aquí.',
  },
];

/** Los pasos que cierran: las herramientas que acompañan a todas las páginas. */
const GENERALES_FIN: PasoRecorrido[] = [
  /*
   * Las páginas del módulo, DOS VECES: el menú fijo de escritorio y el botón ☰
   * que lo abre bajo `lg`. No es duplicar el paso — son dos elementos distintos
   * y en cada pantalla solo uno está a la vista, así que el filtro por presencia
   * deja pasar exactamente uno (`Recorrido.tsx`, decisión 2). Con un solo paso,
   * la mitad de los usuarios —que entran desde el teléfono— se quedaban sin la
   * explicación de cómo llegar a las páginas.
   */
  {
    id: 'lateral',
    objetivo: '[data-recorrido="lateral"]',
    titulo: 'Las páginas del módulo',
    texto: 'Aquí están las páginas del módulo en el que estás. Las que tienen flecha '
      + 'agrupan varias vistas: al pulsarlas se despliegan.',
  },
  {
    id: 'lateral-movil',
    objetivo: '[data-recorrido="menu-movil"]',
    titulo: 'Las páginas del módulo',
    texto: 'Con este botón se abre la lista de páginas del módulo en el que estás. En una '
      + 'pantalla grande ese mismo menú va fijo a la izquierda, siempre visible.',
  },
  {
    id: 'tasas',
    objetivo: '[data-recorrido="tasas"]',
    titulo: 'Las tasas del día',
    texto: 'La cinta de tasas del día, siempre a la vista bajo el menú. Las cifras del '
      + 'portal están en dólares; esto es para convertir sin salir a buscarlo.',
  },
  {
    id: 'ayuda',
    objetivo: '[data-recorrido="ayuda"]',
    titulo: '¿Cómo funciona?',
    texto: 'Cada bloque de datos tiene este botón, y explica CÓMO se calcula lo que ese '
      + 'bloque muestra: de dónde sale, qué entra y qué no. Está junto a la cifra, para '
      + 'que la duda se resuelva sin preguntar.',
  },
  {
    id: 'dhalia',
    objetivo: '[data-recorrido="dhalia"]',
    titulo: 'DHALIA, la asistente',
    texto: 'Pregúntale con tus palabras: «¿dónde veo mis metas?», «¿cómo va mi cartera?». '
      + 'Te lleva a la sección y te resume tus cifras — solo las tuyas.',
  },
  {
    id: 'clave',
    objetivo: '[data-recorrido="clave"]',
    titulo: 'Tu clave',
    texto: 'Desde aquí cambias tu contraseña cuando quieras. Es tuya: no la comparta '
      + 'con nadie, y si esta cuenta la usan varias personas, avísales cuando la cambies.',
  },
  {
    id: 'boton-recorrido',
    objetivo: '[data-recorrido="boton-recorrido"]',
    titulo: 'Este recorrido, cuando lo necesites',
    texto: 'Este botón repite el recorrido en cualquier momento. No hace falta que te '
      + 'lo aprendas hoy.',
  },
  {
    id: 'salir',
    objetivo: '[data-recorrido="salir"]',
    titulo: 'Salir',
    texto: 'Cierra tu sesión. Hazlo siempre si el computador lo usa alguien más: mientras '
      + 'la sesión esté abierta, quien se siente delante ve tu portal.',
  },
  {
    id: 'cierre',
    titulo: 'Listo',
    texto: 'Eso es todo. Si algo no cuadra o falta una sección que necesitas, dilo: el '
      + 'portal se ajusta a lo que pide el negocio, no al contrario.',
  },
];

/**
 * El guion completo, en orden. Los módulos van en el orden de `MODULOS`
 * (`lib/navegacion.ts`), que es el mismo de la barra de arriba: el recorrido
 * recorre la pantalla de izquierda a derecha y no salta.
 *
 * El `objetivo` de cada paso de módulo se DERIVA de su id, no se escribe a mano:
 * así el selector y el atributo que pinta `Marco.tsx` no pueden divergir por un
 * error de tecleo, y un módulo nuevo queda anclado con solo añadir su texto.
 */
export const PASOS: PasoRecorrido[] = [
  ...GENERALES_INICIO,
  ...MODULOS.flatMap((m) => {
    const paso = POR_MODULO.find((p) => p.modulo === m.id);
    return paso ? [{ ...paso, objetivo: `[data-recorrido-mod="${m.id}"]` }] : [];
  }),
  ...GENERALES_FIN,
];

/** Los pasos por módulo tal como se escribieron, para la prueba. */
export const PASOS_POR_MODULO = POR_MODULO;

/**
 * Los pasos que este usuario va a ver de verdad, en este momento.
 *
 * Dos filtros, y los dos son necesarios:
 *  · POR PERMISO — un paso de módulo solo se ofrece a quien tiene el módulo. Sin
 *    esto, a un asesor se le explicarían las 14 secciones y 10 de ellas le darían
 *    403. Es la misma regla que el menú (`modulosVisibles`).
 *  · POR PRESENCIA — el elemento tiene que estar en la pantalla AHORA. El menú
 *    lateral no existe en un módulo de una sola página, la cinta de tasas puede
 *    no haber cargado y el botón «¿Cómo funciona?» solo está en las páginas que
 *    tienen bloques con ayuda. Un paso que señala la nada es peor que no tenerlo.
 *
 * `existe` se inyecta (en la aplicación es `document.querySelector`) para que
 * esto se pueda probar sin navegador.
 */
export function pasosVisibles(
  tieneModulo: (id: string) => boolean,
  existe: (selector: string) => boolean,
  pasos: readonly PasoRecorrido[] = PASOS,
): PasoRecorrido[] {
  return pasos.filter((p) => {
    if (p.modulo && !tieneModulo(p.modulo)) return false;
    if (p.objetivo && !existe(p.objetivo)) return false;
    return true;
  });
}

/**
 * Los minutos que se anuncian en el saludo. Se calcula del número de pasos que
 * le van a salir A ESTE usuario —no es un rótulo fijo— porque la diferencia es
 * real: 9 pasos para un asesor, 22 para MASTER. Prometer «1 minuto» y tardar
 * tres es la clase de detalle que hace que nadie vuelva a pulsar el botón.
 * ~9 segundos por paso, medido leyendo en voz alta los textos más largos.
 */
export function minutosDe(nPasos: number): number {
  return Math.max(1, Math.round((nPasos * 9) / 60));
}
